/**
 * Download utility functions for handling image downloads
 */

/**
 * Downloads an image from a URL
 * @param {string} imageUrl - The URL of the image to download
 * @param {string} filename - Optional custom filename (defaults to generated name)
 * @returns {Promise<void>}
 */
export const downloadImage = async (imageUrl, filename = null) => {
  try {
    // Determine file extension from URL
    let extension = getExtensionFromUrl(imageUrl);
    
    // Generate filename if not provided
    if (!filename) {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      filename = `generated-image-${timestamp}${extension}`;
    } else if (!filename.includes('.')) {
      filename += extension;
    }

    // Try to fetch the image with proper CORS handling
    try {
      const response = await fetch(imageUrl, {
        mode: 'cors',
        headers: {
          'Accept': 'image/*'
        }
      });

      if (response.ok) {
        // If fetch succeeds, use blob method
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true, filename };
      }
    } catch (fetchError) {
      console.log('Fetch failed, trying direct download method:', fetchError.message);
    }

    // Fallback: Direct download method (works for many cases)
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename;
    link.target = '_blank'; // Open in new tab as fallback
    link.rel = 'noopener noreferrer';
    
    // Add to DOM temporarily
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return { success: true, filename };
  } catch (error) {
    console.error('Error downloading image:', error);
    throw new Error(`Download failed: ${error.message}`);
  }
};

/**
 * Get file extension from content type
 * @param {string} contentType - The content type header
 * @returns {string} File extension with dot
 */
const getExtensionFromContentType = (contentType) => {
  const typeMap = {
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'image/gif': '.gif',
    'image/webp': '.webp',
    'image/svg+xml': '.svg',
    'image/bmp': '.bmp',
    'image/tiff': '.tiff'
  };
  
  return typeMap[contentType.toLowerCase()] || '';
};

/**
 * Get file extension from URL
 * @param {string} url - The image URL
 * @returns {string} File extension with dot
 */
const getExtensionFromUrl = (url) => {
  try {
    const pathname = new URL(url).pathname;
    const extension = pathname.substring(pathname.lastIndexOf('.'));
    
    // Validate extension
    const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.tiff'];
    if (validExtensions.includes(extension.toLowerCase())) {
      return extension.toLowerCase();
    }
  } catch (error) {
    console.warn('Could not parse URL for extension:', error);
  }
  
  // Default to .jpg if no extension found
  return '.jpg';
};

/**
 * Download multiple images as a zip file (future enhancement)
 * @param {Array} imageUrls - Array of image URLs
 * @param {string} zipFilename - Name for the zip file
 */
export const downloadImagesAsZip = async (imageUrls, zipFilename = 'generated-images.zip') => {
  // This would require a zip library like JSZip
  // For now, we'll download images individually
  const downloadPromises = imageUrls.map((url, index) => 
    downloadImage(url, `image-${index + 1}`)
  );
  
  try {
    await Promise.all(downloadPromises);
    return { success: true, count: imageUrls.length };
  } catch (error) {
    console.error('Error downloading multiple images:', error);
    throw error;
  }
};

/**
 * Check if the browser supports the download functionality
 * @returns {boolean}
 */
export const isDownloadSupported = () => {
  return typeof document !== 'undefined' && 'download' in document.createElement('a');
};